import { generateAccessName, AccessKey } from "../../../../access";

/**
 * Base keys of the access sections on this page.
 */
const ACCESS_SECTION: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.WIDGET_ORDER_MAP,
];

const ACCESS_SECTION_FILTER: AccessKey[] = [
	AccessKey.ORDERS,
	AccessKey.WIDGET_ORDER_MAP,
	AccessKey.FILTER,
];

/**
 * Enum representing special keys for each tab on this page.
 * These keys are used to uniquely identify and manage individual tabs.
 */
enum TabKeys {
	SEARCH = AccessKey.SEARCH,
	FILTER = AccessKey.FILTER,
	MAP_FILTER_POINTS = AccessKey.MAP_FILTER_POINTS,
	MAP_FILTER_PRICE_ZONE = AccessKey.MAP_FILTER_PRICE_ZONE,
	MAP_FILTER_SECTOR = AccessKey.MAP_FILTER_SECTOR,
	MAP_FILTER_PARKING = AccessKey.MAP_FILTER_PARKING,
	MAP_FILTER_EXECUTOR = AccessKey.MAP_FILTER_EXECUTOR,
	MAP_FILTER_CLIENT = AccessKey.MAP_FILTER_CLIENT,
	//
	COMPANIES_AND_TAXI_SERVICES = AccessKey.COMPANIES_AND_TAXI_SERVICES,
	CAR_PARKS = AccessKey.CAR_PARKS,
	EXECUTOR_TEAMS = AccessKey.EXECUTOR_TEAMS,
	//
	BY_ORDER = AccessKey.BY_ORDER,
	STATUS = AccessKey.STATUS,
	IS_WORKING = AccessKey.IS_WORKING,
}

/**
 * Constructs an array of access addresses by combining base access addresses
 * with the tab-specific access keys. This builds the full set of addresses
 * needed to manage access from the page level to the specific tab.
 *
 * @param key - Tab-specific access keys.
 * @returns Array of access addresses including base and tab-specific addresses.
 */
const generateAccessSection = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION,
	key,
];
const generateAccessSectionFilter = (key: AccessKey): AccessKey[] => [
	...ACCESS_SECTION_FILTER,
	key,
];

/**
 * Maps each tab on the page to its corresponding array of access addresses.
 * This container is used for managing access control not only for the tabs themselves,
 * but also for internal elements such as buttons within these tabs.
 * Each entry provides a set of access addresses specific to the tab.
 */
const ARR_ACCESS_PART_KEY: Record<TabKeys, AccessKey[]> = {
	[TabKeys.SEARCH]: generateAccessSection(AccessKey.SEARCH),
	[TabKeys.FILTER]: generateAccessSection(AccessKey.FILTER),

	[TabKeys.MAP_FILTER_POINTS]: generateAccessSection(
		AccessKey.MAP_FILTER_POINTS,
	),
	[TabKeys.MAP_FILTER_PRICE_ZONE]: generateAccessSection(
		AccessKey.MAP_FILTER_PRICE_ZONE,
	),
	[TabKeys.MAP_FILTER_SECTOR]: generateAccessSection(
		AccessKey.MAP_FILTER_SECTOR,
	),
	[TabKeys.MAP_FILTER_PARKING]: generateAccessSection(
		AccessKey.MAP_FILTER_PARKING,
	),
	[TabKeys.MAP_FILTER_EXECUTOR]: generateAccessSection(
		AccessKey.MAP_FILTER_EXECUTOR,
	),
	[TabKeys.MAP_FILTER_CLIENT]: generateAccessSection(
		AccessKey.MAP_FILTER_CLIENT,
	),
	//
	[TabKeys.COMPANIES_AND_TAXI_SERVICES]: generateAccessSectionFilter(
		AccessKey.COMPANIES_AND_TAXI_SERVICES,
	),
	[TabKeys.CAR_PARKS]: generateAccessSectionFilter(AccessKey.CAR_PARKS),
	[TabKeys.EXECUTOR_TEAMS]: generateAccessSectionFilter(
		AccessKey.EXECUTOR_TEAMS,
	),
	[TabKeys.BY_ORDER]: generateAccessSectionFilter(AccessKey.BY_ORDER),
	[TabKeys.STATUS]: generateAccessSectionFilter(AccessKey.STATUS),
	[TabKeys.IS_WORKING]: generateAccessSectionFilter(AccessKey.IS_WORKING),
};

/**
 * Type representing the access names for each tab.
 */
type TabAccessNamesType = {
	[key in TabKeys]: string;
};

/**
 * Generates access names from arrays of access keys for each tab.
 * This mapping creates a unique access name for each tab, which is used
 * to determine whether the tab is accessible or not. Each access name represents
 * the combined access keys for the tab.
 *
 * @returns TabAccessNamesType - An object mapping each tab key to its unique access name
 */
const TabAccessNames: TabAccessNamesType = Object.fromEntries(
	Object.entries(ARR_ACCESS_PART_KEY).map(([key, section]) => [
		key,
		generateAccessName(...section),
	]),
) as TabAccessNamesType;

export { TabKeys, TabAccessNames, ARR_ACCESS_PART_KEY };
